// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState } from "react"
import { useUserSelf } from "@/queries/queries.users"
import {
  useAdminCreditRequests,
  useApproveCreditRequest,
  useRejectCreditRequest,
  type AdminCreditRequestItem,
} from "@/queries/queries.creditRequests"

// ADR-DRAFT-credit-request-landing: gate on `User.Role` client-side for UX
// only — the endpoints stay the authorization truth. A non-admin sees
// nothing: the list query never fires (`enabled` withheld) rather than
// firing and hiding the result.

const isAdmin = (user: User | undefined): boolean => user?.role === "ADMIN"

export default function AdminCreditRequestsView() {
  const { data: user, isSuccess: userLoaded } = useUserSelf()
  const admin = isAdmin(user)
  const { data: requests } = useAdminCreditRequests({
    enabled: userLoaded && admin,
  })

  if (!userLoaded || !admin) return null

  return (
    <div className="flex flex-col gap-5 max-w-[900px]">
      <div className="flex flex-col gap-[7px] border-b border-border pb-5">
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-tertiary">
          Admin
        </span>
        <h1 className="text-[22px] font-semibold text-foreground">
          Credit request review
        </h1>
      </div>

      {requests && requests.length === 0 && (
        <p className="text-[13px] text-text-secondary">
          No pending requests.
        </p>
      )}

      {requests && requests.length > 0 && (
        <ul aria-label="Pending credit requests" className="flex flex-col gap-4">
          {requests.map((request) => (
            <CreditRequestRow key={request.id} request={request} />
          ))}
        </ul>
      )}
    </div>
  )
}

const CreditRequestRow = ({
  request,
}: {
  request: AdminCreditRequestItem
}) => {
  const [grantedAmount, setGrantedAmount] = useState(
    String(request.requested_amount),
  )
  const [amountError, setAmountError] = useState("")
  const approve = useApproveCreditRequest()
  const reject = useRejectCreditRequest()

  const handleApprove = () => {
    const amount = Number(grantedAmount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setAmountError("Enter an amount greater than zero.")
      return
    }
    setAmountError("")
    approve.mutate({ id: request.id, grantedAmount: amount })
  }

  return (
    <li
      aria-label={`credit request ${request.id}`}
      className="flex flex-col gap-2 rounded-[12px] border border-border-strong p-4"
    >
      <div className="flex flex-col gap-1">
        <span className="text-[13px] text-foreground">{request.use_case}</span>
        <span className="text-[12px] text-text-tertiary">
          {request.profile} — {request.current_usage}
        </span>
        <span className="text-[12px] text-text-tertiary">
          {request.email}
          {request.github_username ? ` (${request.github_username})` : ""} —
          balance {request.balance} — {request.tier}
        </span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[12px] text-text-tertiary">
          Granted amount
        </span>
        <input
          aria-label={`Granted amount for request ${request.id}`}
          type="number"
          value={grantedAmount}
          onChange={(e) => setGrantedAmount(e.target.value)}
          className="h-9 w-32 rounded-[10px] border border-border-strong bg-[rgba(4,20,24,0.5)] px-3 text-[13px] text-foreground"
        />
        {amountError && <span role="alert">{amountError}</span>}
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleApprove}
          disabled={approve.isPending}
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => reject.mutate(request.id)}
          disabled={reject.isPending}
        >
          Reject
        </button>
      </div>
    </li>
  )
}
